import { TrashSimpleIcon, WarningIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../services/toast-service.tsx'
import { useDeleteCodingGuideline } from '../../utils/api-client/api-hooks.ts'
import { type CodingGuidelinesResponse } from '../../utils/api-client/api-schemas.ts'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import { DOC_TYPE_META, getDocType } from './coding-guidelines-types.ts'
import * as styles from './delete-confirm-modal.css.ts'
import { DocTypeIcon } from './doc-type-icon.tsx'

export type DeleteConfirmModalProps = {
  organizationId?: string
  document: CodingGuidelinesResponse
  onCancel: () => unknown
  onSuccess: () => unknown
}

export const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  organizationId,
  document,
  onCancel,
  onSuccess,
}) => {
  const { addToast } = useToastService()
  const showError = useShowMutationError({ prefix: 'Failed to delete document' })
  const deleteMutation = useDeleteCodingGuideline({ organizationId, onError: showError })

  const handleDelete = () => {
    const id = document.id
    if (!id) {
      return
    }
    deleteMutation.mutate(
      { id },
      {
        onSuccess: () => {
          addToast({
            message: <>&ldquo;{document.display_name ?? 'Document'}&rdquo; was deleted</>,
            variant: 'success',
          })
          onSuccess()
        },
      }
    )
  }

  return (
    <Dialog.Root
      open
      onOpenChange={open => {
        if (!open) {
          onCancel()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} onClick={onCancel} />
        <Dialog.Content className={styles.content} onPointerDownOutside={e => e.preventDefault()}>
          <Dialog.Title className={styles.title}>
            <WarningIcon size={28} weight="light" className={styles.icon} />
            Delete document?
          </Dialog.Title>
          <Dialog.Description asChild>
            <p className={styles.description}>You are about to delete the following document:</p>
          </Dialog.Description>
          <DocumentSummaryCard document={document} />
          <p className={styles.description}>This action cannot be undone. Do you wish to proceed?</p>
          <div className={styles.buttons}>
            <GhostButton
              size="small"
              onClick={onCancel}
              state={deleteMutation.isPending ? 'disabled' : 'default'}
              className={styles.cancelBtn}
            >
              Cancel
            </GhostButton>
            <DefaultButton
              buttonType="destructive"
              size="small"
              state={deleteMutation.isPending ? 'loading' : 'default'}
              onClick={handleDelete}
              loadingLabel="Deleting…"
              icon={<TrashSimpleIcon size={14} weight="light" />}
            >
              Delete document
            </DefaultButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

const DocumentSummaryCard: React.FC<{ document: CodingGuidelinesResponse }> = ({ document }) => {
  const docType = document.filename ? getDocType(document.filename) : undefined
  const typeLabel = docType ? DOC_TYPE_META[docType].label : undefined

  return (
    <div className={styles.documentCard}>
      <div className={styles.documentCardHeader}>
        <DocTypeIcon docType={docType} size={16} />
        <span>
          {typeLabel ? `${typeLabel} · ` : ''}
          {document.filename ?? ''}
        </span>
      </div>
      <p className={styles.documentName}>{document.display_name}</p>
    </div>
  )
}
